import React, { useEffect, useState, useCallback, useRef } from "react";
import { adminFeed, adminChatReply, adminEmailDispatch } from "../lib/api";
import { Crown } from "./CrownIcon";
import {
    Send,
    Mail,
    MessageSquare,
    RefreshCw,
    Inbox,
    Radio,
    Bell,
    BellOff,
    Volume2,
    VolumeX,
} from "lucide-react";
import { toast } from "sonner";

const SUBJECT_DEFAULT = "📡 Direct Comm from Leonard";
const LAST_SEEN_KEY = "gsb_hub_last_seen";
const MUTE_KEY = "gsb_hub_muted";

const supportsNotif = typeof window !== "undefined" && "Notification" in window;

// Soft bell ding using Web Audio API — two-tone, exponential decay
let _audioCtx = null;
const playDing = () => {
    if (typeof window === "undefined") return;
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!_audioCtx) _audioCtx = new Ctx();
        if (_audioCtx.state === "suspended") _audioCtx.resume();
        const now = _audioCtx.currentTime;
        const tones = [
            { freq: 988, start: 0, dur: 0.6 }, // B5
            { freq: 1319, start: 0.08, dur: 0.55 }, // E6
        ];
        tones.forEach(({ freq, start, dur }) => {
            const osc = _audioCtx.createOscillator();
            const gain = _audioCtx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, now + start);
            gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
            osc.connect(gain).connect(_audioCtx.destination);
            osc.start(now + start);
            osc.stop(now + start + dur + 0.05);
        });
    } catch {
        /* ignore */
    }
};

export const CommsHub = ({ adminKey }) => {
    const [feed, setFeed] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(null);
    const [method, setMethod] = useState("chat");
    const [reply, setReply] = useState("");
    const [subject, setSubject] = useState(SUBJECT_DEFAULT);
    const [dispatching, setDispatching] = useState(false);
    const [notifPerm, setNotifPerm] = useState(
        supportsNotif ? Notification.permission : "unsupported",
    );
    const [muted, setMuted] = useState(
        () => localStorage.getItem(MUTE_KEY) === "1",
    );
    const lastSeenRef = useRef(localStorage.getItem(LAST_SEEN_KEY) || "");
    const initRef = useRef(false);
    const mutedRef = useRef(muted);
    useEffect(() => {
        mutedRef.current = muted;
        localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    }, [muted]);

    const fireNotif = useCallback((item) => {
        if (!mutedRef.current) playDing();
        if (!supportsNotif || Notification.permission !== "granted") return;
        const tag = `gsb-${item.kind}-${item.id}`;
        try {
            const n = new Notification(
                item.kind === "dm" ? "🙏 New DM" : "💬 New chat",
                {
                    body: `${item.name}: ${item.message.slice(0, 120)}`,
                    tag,
                    icon: "/preacher-portrait.png",
                    badge: "/preacher-portrait.png",
                    requireInteraction: item.kind === "dm",
                },
            );
            n.onclick = () => {
                window.focus();
                setActive(item);
                n.close();
            };
        } catch (e) {
            // ignore
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminFeed(adminKey);
            setFeed(data);
            // Fire notifications for items newer than lastSeen — but skip the first load
            if (initRef.current) {
                const fresh = data.filter(
                    (d) =>
                        d.kind === "dm" &&
                        d.created_at > (lastSeenRef.current || ""),
                );
                fresh.slice(0, 3).forEach(fireNotif);
            }
            if (data[0]) {
                lastSeenRef.current = data[0].created_at;
                localStorage.setItem(LAST_SEEN_KEY, data[0].created_at);
            }
            initRef.current = true;
        } catch {
            toast.error("Failed to load feed");
        } finally {
            setLoading(false);
        }
    }, [adminKey, fireNotif]);

    useEffect(() => {
        load();
        const id = setInterval(load, 6000);
        return () => clearInterval(id);
    }, [load]);

    const enableNotifs = async () => {
        if (!supportsNotif) {
            toast.error("Notifications not supported in this browser");
            return;
        }
        try {
            const perm = await Notification.requestPermission();
            setNotifPerm(perm);
            if (perm === "granted") {
                toast.success("Notifications ON — you'll get pinged on new DMs");
                new Notification("🙏 Comms Hub armed", {
                    body: "You'll get a ping whenever a fan slides into the DMs.",
                    icon: "/preacher-portrait.png",
                });
            } else if (perm === "denied") {
                toast.error(
                    "Permission denied — unblock notifications in your browser settings",
                );
            }
        } catch {
            toast.error("Could not enable notifications");
        }
    };

    // If active item lacks email, force method to chat
    useEffect(() => {
        if (active && !active.email && method === "email") setMethod("chat");
    }, [active, method]);

    const dispatch = async () => {
        if (!active || !reply.trim()) {
            toast.error("Pick a message and type a reply");
            return;
        }
        setDispatching(true);
        try {
            if (method === "chat") {
                await adminChatReply(
                    {
                        message: reply.trim(),
                        reply_to_username: active.name,
                    },
                    adminKey,
                );
                toast.success("Posted to live chat");
            } else {
                if (!active.email) {
                    toast.error("This message has no email");
                    return;
                }
                await adminEmailDispatch(
                    {
                        to_email: active.email,
                        to_name: active.name,
                        subject: subject || SUBJECT_DEFAULT,
                        body: reply.trim(),
                    },
                    adminKey,
                );
                toast.success(`GTA email dispatched to ${active.email}`);
            }
            setReply("");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Dispatch failed. Try again.",
            );
        } finally {
            setDispatching(false);
        }
    };

    return (
        <div className="space-y-5">
            {/* Top bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex items-center gap-2 bg-black border-2 border-kickgreen px-3 py-1.5">
                    <Radio
                        className="w-4 h-4 text-kickgreen animate-pulse"
                        strokeWidth={3}
                    />
                    <span className="font-anton uppercase tracking-widest text-xs text-white">
                        Producer Comms Hub
                    </span>
                </div>
                <NotifPill
                    perm={notifPerm}
                    onEnable={enableNotifs}
                />
                <button
                    data-testid="audio-toggle"
                    onClick={() => {
                        const next = !muted;
                        setMuted(next);
                        if (!next) playDing(); // preview the ding when un-muting
                        toast.message(next ? "Sound muted" : "Sound on — soft ding test 🔔");
                    }}
                    title={muted ? "Sound is muted — click to enable ding" : "Sound is on — click to mute"}
                    className={`inline-flex items-center gap-1 font-anton uppercase text-xs px-3 py-1.5 border-2 transition-colors ${
                        muted
                            ? "border-white/20 text-white/40 hover:border-skyblue hover:text-skyblue"
                            : "border-skyblue text-skyblue hover:bg-skyblue hover:text-black"
                    }`}
                >
                    {muted ? (
                        <VolumeX className="w-3 h-3" />
                    ) : (
                        <Volume2 className="w-3 h-3" strokeWidth={3} />
                    )}
                    {muted ? "Muted" : "Ding ON"}
                </button>
                <button
                    onClick={load}
                    className="ml-auto inline-flex items-center gap-2 border-2 border-skyblue text-skyblue font-anton uppercase text-xs px-3 py-1.5 hover:bg-skyblue hover:text-black transition-colors"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                {/* Feed */}
                <div className="lg:col-span-3 bg-surface border-4 border-black brutal-shadow-blue flex flex-col h-[600px]">
                    <div className="px-4 py-3 border-b-2 border-white/10 flex items-center gap-2">
                        <Inbox className="w-4 h-4 text-skyblue" />
                        <span className="font-anton uppercase tracking-wider text-sm">
                            Unified Feed
                        </span>
                        <span className="ml-auto text-xs font-chivo text-white/40">
                            {feed.length} items
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {feed.length === 0 ? (
                            <p className="font-chivo text-white/40 text-sm text-center py-10">
                                {loading ? "Loading…" : "No messages yet."}
                            </p>
                        ) : (
                            feed.map((item) => (
                                <FeedItem
                                    key={`${item.kind}-${item.id}`}
                                    item={item}
                                    active={
                                        active?.id === item.id &&
                                        active?.kind === item.kind
                                    }
                                    onSelect={() => setActive(item)}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Dispatch panel */}
                <div className="lg:col-span-2 bg-surface border-4 border-black brutal-shadow-pink p-5 space-y-4">
                    <div className="flex items-center gap-2">
                        <Crown className="w-7 h-5" color="#FF2E93" />
                        <span className="font-anton uppercase tracking-widest text-xs text-white">
                            Dispatch Response
                        </span>
                    </div>

                    {!active ? (
                        <p className="font-chivo text-white/40 text-sm py-12 text-center border-2 border-dashed border-white/10">
                            Tap a message in the feed to start a reply.
                        </p>
                    ) : (
                        <>
                            <div className="bg-black border-2 border-white/15 p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <KindBadge kind={active.kind} />
                                    <span className="font-anton uppercase tracking-wider text-sm text-white truncate">
                                        {active.name}
                                    </span>
                                </div>
                                {active.email && (
                                    <div className="font-chivo text-xs text-skyblue truncate">
                                        {active.email}
                                    </div>
                                )}
                                <p className="font-chivo text-sm text-white/80 mt-2 line-clamp-3">
                                    {active.message}
                                </p>
                            </div>

                            <div>
                                <label className="block font-anton uppercase text-[10px] tracking-widest text-white/60 mb-1">
                                    Send via
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <MethodBtn
                                        active={method === "chat"}
                                        onClick={() => setMethod("chat")}
                                        icon={MessageSquare}
                                        label="Live Chat"
                                        tone="green"
                                    />
                                    <MethodBtn
                                        active={method === "email"}
                                        onClick={() => setMethod("email")}
                                        icon={Mail}
                                        label="GTA Email"
                                        tone="pink"
                                        disabled={!active.email}
                                    />
                                </div>
                                {method === "email" && !active.email && (
                                    <p className="font-chivo text-xs text-hotpink mt-1">
                                        No email on this message — pick Live Chat.
                                    </p>
                                )}
                            </div>

                            {method === "email" && (
                                <div>
                                    <label className="block font-anton uppercase text-[10px] tracking-widest text-white/60 mb-1">
                                        Subject
                                    </label>
                                    <input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white focus:border-hotpink focus:outline-none"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block font-anton uppercase text-[10px] tracking-widest text-white/60 mb-1">
                                    Your reply
                                </label>
                                <textarea
                                    data-testid="hub-reply-input"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    rows={method === "email" ? 6 : 3}
                                    maxLength={method === "chat" ? 500 : 4000}
                                    placeholder={
                                        method === "chat"
                                            ? `Reply to @${active.name} in the live chat…`
                                            : `Write a personal email to ${active.name}…`
                                    }
                                    className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white placeholder:text-white/30 focus:border-kickgreen focus:outline-none resize-none"
                                />
                                <div className="text-right text-[10px] text-white/40 font-chivo mt-1">
                                    {reply.length}/{method === "chat" ? 500 : 4000}
                                </div>
                            </div>

                            <button
                                data-testid="hub-dispatch-btn"
                                onClick={dispatch}
                                disabled={dispatching || !reply.trim()}
                                className="w-full inline-flex items-center justify-center gap-2 bg-kickgreen text-black font-anton uppercase tracking-wider text-lg py-3 border-2 border-black hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#FF2E93] transition-all disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" strokeWidth={3} />
                                {dispatching ? "Dispatching…" : "Dispatch Response"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const KindBadge = ({ kind }) => {
    if (kind === "dm") {
        return (
            <span className="inline-block bg-hotpink text-white font-anton uppercase tracking-widest text-[10px] px-2 py-0.5 border-2 border-black">
                DM
            </span>
        );
    }
    return (
        <span className="inline-block bg-skyblue text-black font-anton uppercase tracking-widest text-[10px] px-2 py-0.5 border-2 border-black">
            Chat
        </span>
    );
};

const FeedItem = ({ item, active, onSelect }) => (
    <button
        data-testid="hub-feed-item"
        onClick={onSelect}
        className={`w-full text-left p-3 border-2 transition-colors ${
            active
                ? "border-kickgreen bg-kickgreen/10"
                : "border-white/10 hover:border-white/30 bg-black"
        }`}
    >
        <div className="flex items-center gap-2 mb-1.5">
            <KindBadge kind={item.kind} />
            <span className="font-anton uppercase tracking-wider text-sm text-white truncate">
                {item.name}
            </span>
            {item.email && (
                <span className="font-chivo text-[10px] text-skyblue truncate">
                    {item.email}
                </span>
            )}
            <span className="ml-auto font-chivo text-[10px] text-white/40 whitespace-nowrap">
                {new Date(item.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                })}
            </span>
        </div>
        <p className="font-chivo text-xs text-white/70 line-clamp-2">
            {item.message}
        </p>
    </button>
);

const methodTones = {
    green: {
        on: "bg-kickgreen text-black border-black",
        off: "border-kickgreen text-kickgreen",
    },
    pink: {
        on: "bg-hotpink text-white border-black",
        off: "border-hotpink text-hotpink",
    },
};

const MethodBtn = ({ active, onClick, icon: Icon, label, tone, disabled }) => {
    const styles = methodTones[tone];
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 font-anton uppercase tracking-wider text-sm px-3 py-2 border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active ? styles.on : `bg-black ${styles.off}`
            }`}
        >
            <Icon className="w-4 h-4" strokeWidth={2.5} />
            {label}
        </button>
    );
};

const NotifPill = ({ perm, onEnable }) => {
    if (perm === "unsupported") {
        return (
            <span
                data-testid="notif-pill"
                title="Notifications not supported in this browser"
                className="inline-flex items-center gap-1 border-2 border-white/20 text-white/40 font-anton uppercase text-xs px-3 py-1.5"
            >
                <BellOff className="w-3 h-3" /> N/A
            </span>
        );
    }
    if (perm === "granted") {
        return (
            <span
                data-testid="notif-pill-on"
                title="Browser notifications are on — you'll be pinged on new DMs"
                className="inline-flex items-center gap-1 bg-kickgreen text-black font-anton uppercase text-xs px-3 py-1.5 border-2 border-black"
            >
                <Bell className="w-3 h-3" strokeWidth={3} /> Pings ON
            </span>
        );
    }
    if (perm === "denied") {
        return (
            <span
                data-testid="notif-pill-denied"
                title="Permission was denied — unblock notifications in your browser settings"
                className="inline-flex items-center gap-1 border-2 border-hotpink text-hotpink font-anton uppercase text-xs px-3 py-1.5"
            >
                <BellOff className="w-3 h-3" /> Blocked
            </span>
        );
    }
    return (
        <button
            data-testid="notif-pill-enable"
            onClick={onEnable}
            title="Get a browser ping when a new DM arrives"
            className="inline-flex items-center gap-1 border-2 border-kickgreen text-kickgreen font-anton uppercase text-xs px-3 py-1.5 hover:bg-kickgreen hover:text-black transition-colors"
        >
            <Bell className="w-3 h-3" strokeWidth={3} /> Enable Pings
        </button>
    );
};
